import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { setModalAnexo } from "../../../../redux/slices/arquivoAnexo/arquivoAnexoSlice";
import { useAppDispatch } from "../../../../util/hooks";
import { Button, Col, Flex, Form, FormInstance, Input, Popconfirm, Row, Space, Table, Tooltip, Typography, Upload, UploadFile, UploadProps } from "antd";
import React, { useState } from "react";
import { CheckOutlined, CloseOutlined, PlusCircleOutlined, UploadOutlined } from "@ant-design/icons";
import { ArquivoAnexoModel } from "../../../../models/arquivoAnexo/arquivoAnexoModel";
import { cadastrarArquivoAnexoSorteio } from "../../../../services/arquivoAnexoService";
import { toastWarning } from "../../../../util/toast";
import * as PhosphorIcon from "phosphor-react";

dayjs.locale("pt-br");
interface AnexoModel {
  key: string;
  arquivo: UploadFile;
  caminhoArquivo: string | null;
  observacao: string | null;
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "image" | "string";
  record: AnexoModel;
  index: number;
  children: React.ReactNode;
}

type CadastrarAnexoModalProps = {
  idOrigem: number;
};

const CadastrarAnexoModal = ({ idOrigem }: CadastrarAnexoModalProps) => {
  //estados globais

  //hooks
  const dispatch = useAppDispatch();

  //estados locais
  const [form] = Form.useForm();
  const [formManual] = Form.useForm();
  const [count, setCount] = useState<number>(0);
  const [isEmpty, setIsEmpty] = useState(false);
  const [dataSource, setDataSource] = useState<AnexoModel[]>([]);
  const [editingKey, setEditingKey] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const handleImageChange: UploadProps["onChange"] = ({ fileList: newFileList }) => {
    if (newFileList.length === 0) {
      formManual.setFieldValue("arquivo", undefined);
      setIsEmpty(true);
    } else {
      var arquivo = newFileList[0];
      formManual.setFieldValue("arquivo", arquivo);
      setIsEmpty(false);
    }
  };

  const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({ editing, dataIndex, title, inputType, record, index, children, ...restProps }) => {
    const inputNode =
      inputType === "image" ? (
        <Upload
          defaultFileList={formManual.getFieldValue("arquivo") !== undefined ? [formManual.getFieldValue("arquivo")] : []}
          action={"dummyRequest"}
          onChange={handleImageChange}
          beforeUpload={() => {
            return false;
          }}
        >
          {formManual.getFieldValue("arquivo") === undefined || isEmpty ? <Button icon={<UploadOutlined />}>anexar</Button> : <></>}
        </Upload>
      ) : (
        <Input type="text" style={{ width: 300 }} />
      );

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                message: `Campo obrigatório`,
              },
            ]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const isEditing = (record: AnexoModel) => record.key === editingKey;

  const edit = (record: Partial<AnexoModel> & { key: React.Key }) => {
    formManual.setFieldsValue({
      arquivo: record.arquivo,
      observacao: record.observacao,
    });
    setEditingKey(record.key);
  };

  const handleDelete = (key: React.Key) => {
    const newData = dataSource.filter((item: any) => item.key !== key);
    setCount(count - 1);
    setDataSource(newData);
  };

  const cancel = (key: React.Key) => {
    const hasData = dataSource.find((item: any) => item.key === key);
    if (hasData?.observacao === null) {
      handleDelete(key);
    }
    setEditingKey("");
  };

  const handleAdd = () => {
    if (editingKey === "") {
      var contadorSoma = count + 1;
      const newData: AnexoModel = {
        key: contadorSoma.toString(),
        arquivo: {} as UploadFile,
        caminhoArquivo: null,
        observacao: null,
      };
      setDataSource([...dataSource, newData]);
      setEditingKey(contadorSoma.toString());
      setCount(contadorSoma);
      formManual.resetFields();
    } else {
      toastWarning("Para adicionar uma novo item, salve primeiro o item que já está em edição.");
    }
  };

  const save = async (key: React.Key) => {
    try {
      const row = (await formManual.validateFields()) as AnexoModel;
      const newData = [...dataSource];
      const index = newData.findIndex((item) => key === item.key);
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          ...row,
        });
        setDataSource(newData);
      } else {
        newData.push(row);
        setDataSource(newData);
      }
      setEditingKey("");
    } catch (e) {}
  };

  const columns = [
    {
      title: "NOME DO ANEXO",
      dataIndex: "arquivo",
      editable: true,
      render: (arquivo: UploadFile) => <Upload defaultFileList={[arquivo]} showUploadList={{ showRemoveIcon: false }} />,
    },
    {
      title: "DESCRIÇÃO",
      dataIndex: "observacao",
      editable: true,
      align: "right" as "right",
    },
    {
      title: "AÇÕES",
      dataIndex: "acao",
      render: (_: any, record: AnexoModel) => {
        const editable = isEditing(record);
        return editable ? (
          <Flex justify="center" align="center" gap={4}>
            <Tooltip title="Salvar">
              <PhosphorIcon.CheckCircle style={{ cursor: "pointer" }} size={20} color="#334155" onClick={() => save(record.key)} />
            </Tooltip>
            <Tooltip title="Cancelar">
              <PhosphorIcon.XCircle style={{ cursor: "pointer" }} size={20} color="#334155" onClick={() => cancel(record.key)} />
            </Tooltip>
          </Flex>
        ) : (
          <Flex justify="center" gap={4}>
            <Tooltip title="Editar">
              <PhosphorIcon.Pencil style={{ cursor: "pointer" }} size={20} color="#334155" onClick={() => edit(record)} />
            </Tooltip>
            <Tooltip title="Remover">
              <PhosphorIcon.Trash style={{ cursor: "pointer" }} size={20} color="#334155" onClick={() => handleDelete(record.key)} />
            </Tooltip>
          </Flex>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: AnexoModel) => ({
        record,
        inputType: col.dataIndex === "arquivo" ? "image" : "string",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const submit = (values: any) => {
    var arquivo: ArquivoAnexoModel = {
      id: null,
      idOrigem: idOrigem,
      caminhoArquivo: null,
      extensaoArquivo: null,
      idTipoArquivoAnexo: null,
      observacao: null,
      nome: null,
      arquivoAnexo: [],
      containerName: "anexos",
      dataCriacao: null,
      idUsuario: null,
    };
    if (editingKey === "") {
      dispatch(cadastrarArquivoAnexoSorteio({ value: arquivo, dataSource: dataSource }));
    } else {
      toastWarning("Para adicionar um novo item, salve primeiro o item que já está em edição");
    }
  };

  const voltar = () => {
    dispatch(setModalAnexo(false));
  };

  return (
    <Form form={form} layout="vertical" onFinish={submit}>
      <Row gutter={12}>
        <Col span={2}>
          <Button onClick={handleAdd} type="primary" style={{ marginBottom: 24 }} icon={<PlusCircleOutlined />}>
            Adicionar
          </Button>
        </Col>
      </Row>
      <Form form={formManual} component={false}>
        <Row gutter={12}>
          <Col span={24}>
            <Form.Item>
              <Table
                size="small"
                components={{
                  body: {
                    cell: EditableCell,
                  },
                }}
                rowClassName="editable-row"
                dataSource={dataSource}
                columns={mergedColumns}
                pagination={false}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Row gutter={12}>
        <Col span={24} className="modalFooter">
          <Space size="middle">
            <Button className="cancelButton" onClick={voltar} icon={<CloseOutlined />}>
              FECHAR
            </Button>
            <Button htmlType="submit" loading={loading} icon={<CheckOutlined />} className="buttonPrimary">
              SALVAR
            </Button>
          </Space>
        </Col>
      </Row>
    </Form>
  );
};

export default CadastrarAnexoModal;
